// src/components/VerFotosEntregaButton.js
import React, { useState, useEffect } from 'react';
import { Button, Modal, Table, Spin, Image } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../components/common/CustomModal';

export const Axios = axios.create({
  baseURL: process.env.REACT_APP_APPI_ADDRESS,
});

export default function VerFotosEntregaButton({ idruta, headers }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(false);

  // limpiar URLs al cerrar
  useEffect(() => {
    return () => {
      grouped.forEach(inv =>
        inv.fotos.forEach(f => f.blobUrl && URL.revokeObjectURL(f.blobUrl))
      );
    };
  }, [grouped]);

  const handleOpen = () => {
    setOpen(true);
    setLoading(true);
    Axios.post('sd/getPictureDeliveryByRuta', { idruta }, { headers })
      .then(({ data }) => {
        const raw = Array.isArray(data.data) ? data.data : [];
        // convertir imágenes a blobUrls
        const fotosWithUrl = raw.map(f => {
          const base64 = f.imagen.includes('base64,')
            ? f.imagen.split('base64,')[1]
            : f.imagen;
          const bin = atob(base64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const blob = new Blob([arr], { type: 'image/jpeg' });
          const blobUrl = URL.createObjectURL(blob);
          return { ...f, blobUrl };
        });
        // agrupar por codigo_invoice
        const map = {};
        fotosWithUrl.forEach(f => {
          if (!map[f.codigo_invoice]) {
            map[f.codigo_invoice] = {
              key: f.codigo_invoice,
              codigo_invoice: f.codigo_invoice,
              fotos: []
            };
          }
          map[f.codigo_invoice].fotos.push(f);
        });
        setGrouped(Object.values(map));
      })
      .catch(err => {
        console.error(err);
        setGrouped([]);
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    // liberar URLs
    grouped.forEach(inv =>
      inv.fotos.forEach(f => f.blobUrl && URL.revokeObjectURL(f.blobUrl))
    );
    setGrouped([]);
    setOpen(false);
  };

  // columnas para el listado de facturas
  const invoiceColumns = [
    {
      title: t('foto.col_codigo'),
      dataIndex: 'codigo_invoice',
      key: 'codigo_invoice',
      width: '60%'
    },
    {
      title: t('foto.col_total'),
      key: 'total',
      width: '25%',
      render: (_, rec) => (
        <div style={{
          background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '6px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'inline-block'
        }}>
          {rec.fotos.length} fotos
        </div>
      )
    }
  ];

  // columnas para el sub-table de fotos
  const photoColumns = [
    {
      title: t('foto.col_fecha'),
      dataIndex: 'fechadelivery',
      key: 'fechadelivery'
    },
    {
      title: t('foto.col_observacion'),
      dataIndex: 'observacion',
      key: 'observacion'
    },
    {
      title: t('foto.col_imagen'),
      key: 'imagen',
      render: (_, record) => (
        <Image
          width={100}
          src={record.blobUrl}
          alt="Foto entrega"
          placeholder={<Spin />}
          preview={{ getContainer: () => document.body }}
        />
      )
    }
  ];

  return (
    <>
      <Button
        type="primary"
        icon={<PictureOutlined />}
        onClick={handleOpen}
        disabled={!idruta}
        size="small"
        style={{
          background: idruta
            ? 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)'
            : '#d9d9d9',
          borderColor: idruta
            ? '#253289'
            : '#d9d9d9',
          borderRadius: '8px',
          height: '32px',
          fontWeight: '500',
          color: idruta
            ? 'white'
            : '#000000',
          boxShadow: idruta
            ? '0 2px 8px rgba(37, 50, 137, 0.3)'
            : 'none'
        }}
      >
        {t('ruta.photos_delivery')}
      </Button>
      <CustomModal
        open={open}
        onCancel={handleClose}
        title={t('ruta.photos_delivery')}
        subtitle="Fotografías de evidencia de entrega"
        icon={<PictureOutlined />}
        width={1000}
      >
        <div>
          {/* Contenido del modal */}
          {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <Spin size="large" />
                <p style={{ color: '#666', margin: 0 }}>Cargando fotografías...</p>
              </div>
            ) : (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e0e6ff',
                overflow: 'hidden'
              }}>
                <Table
                  dataSource={grouped}
                  columns={invoiceColumns}
                  rowKey="key"
                  pagination={false}
                  expandable={{
                    expandedRowRender: rec => (
                      <div style={{
                        background: 'linear-gradient(135deg, #f8f9fc 0%, #e8ebf7 100%)',
                        padding: '16px',
                        borderRadius: '8px',
                        margin: '8px'
                      }}>
                        <h4 style={{ color: '#253289', marginBottom: '12px', fontSize: '14px' }}>
                          📷 Fotografías de {rec.codigo_invoice}
                        </h4>
                        <Table
                          dataSource={rec.fotos}
                          columns={photoColumns}
                          rowKey="idpicturedelivery"
                          pagination={false}
                          size="small"
                          style={{
                            background: 'white',
                            borderRadius: '8px'
                          }}
                        />
                      </div>
                    ),
                    rowExpandable: rec => rec.fotos.length > 0,
                    expandRowByClick: true,
                    expandIcon: ({ expanded, onExpand, record }) => (
                      <div
                        onClick={e => onExpand(record, e)}
                        style={{
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: expanded
                            ? 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)'
                            : 'linear-gradient(135deg, #f0f6ff 0%, #e8ebf7 100%)',
                          color: expanded ? 'white' : '#253289',
                          fontSize: '12px',
                          fontWeight: '500',
                          textAlign: 'center',
                          minWidth: '70px',
                          border: expanded ? 'none' : '1px solid #253289',
                          boxShadow: expanded
                            ? '0 2px 8px rgba(37, 50, 137, 0.3)'
                            : '0 1px 4px rgba(37, 50, 137, 0.1)',
                          transition: 'all 0.3s ease',
                          userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!expanded) {
                            e.target.style.background = 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!expanded) {
                            e.target.style.background = 'linear-gradient(135deg, #f0f6ff 0%, #e8ebf7 100%)';
                            e.target.style.color = '#253289';
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {expanded ? '▲ Ocultar' : '▼ Ver'}
                      </div>
                    )
                  }}
                  scroll={{ y: 400 }}
                  style={{
                    background: 'white'
                  }}
                />
              </div>
            )}
        </div>
      </CustomModal>
    </>
  );
}
